import type { UIMainMenuItem } from '@/core/data/config/uiConfig.interface';
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { GeoCoords } from '@/core/data/location/location.interface';
import { StorageKeyType } from '@/core/data/storage/storage.interface';
import NavigationHelper from '@/core/helpers/navigation.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import type { GamNavItemType } from '@/views/composables/models/components/GamNav';
import { addHours, isAfter } from 'date-fns';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

const EXPIRY_POLL_MS = 30_000;
const RECONCILE_GRACE_MS = 15_000;

export interface ActiveCheckIn {
  locationId: string;
  locationName: string;
  coordinates: GeoCoords;
  gambitId?: string;
  checkedInAt: string;
  /** When the check-in session expires. Defaults to checkedInAt + 2h when omitted. */
  endsAt?: string;
}

export const useNavigationStore = defineStore(GamStoreId.NAVIGATION, () => {
  const authStore = useAuthStore();
  const route = useRoute();

  const maxNavItems = ref<number>(5);
  const isCollapsed = ref<boolean>(false);

  const navItems = ref<GamNavItemType[] | null>(null);
  const setNavItems = (mainMenuItems: UIMainMenuItem[] | null) => {
    navItems.value = mainMenuItems?.filter((item) => item.isActive) || [];
  };

  const getNavItems = (): GamNavItemType[] => {
    if (!navItems.value?.length) {
      return [];
    }

    let filteredNavItems = navItems.value;
    if (isMobile.value) {
      if (navItems.value.length > maxNavItems.value) {
        const items: GamNavItemType[] = navItems.value.slice(0, maxNavItems.value - 1);
        const moreItem = NavigationHelper.getMoreNavItem(maxNavItems.value, isShowMoreBubble());
        items.splice(maxNavItems.value - 1, 0, moreItem);
        filteredNavItems = items;
      } else {
        filteredNavItems = navItems.value;
      }
    } else {
      filteredNavItems = navItems.value.filter((item) => !item.showBottom);
    }

    return filteredNavItems.filter((item) => authStore.isAdmin() || !item.isRestrictedToAdmins);
  };

  const getNavItemsBottom = (): GamNavItemType[] => {
    if (navItems.value?.length && !isMobile.value) {
      return navItems.value.filter((item) => item.showBottom && (authStore.isAdmin() || !item.isRestrictedToAdmins));
    }
    return [];
  };

  const getNavItemsMore = (): GamNavItemType[] => {
    if (navItems.value && navItems.value.length > maxNavItems.value && isMobile.value) {
      return navItems.value.slice(maxNavItems.value - 1);
    }
    return [];
  };

  const setMenuMessageCount = (name: RouterNameType, count: number): void => {
    const navItem = navItems.value?.find((item) => item.name === name);
    if (navItem) navItem.messages = { count };
  };

  const isShowMoreBubble = (): boolean => {
    return !!getNavItemsMore()?.some((item) => item.messages?.count);
  };

  const isNavVisible = ref<boolean>(false);
  const setNavVisible = (value: boolean): void => {
    isNavVisible.value = value;
  };

  const isLoading = ref<boolean>(false);
  const setLoading = (value: boolean) => {
    isLoading.value = value;
  };

  const isMoreOpen = ref<boolean>(false);
  const setMoreOpen = (value: boolean): void => {
    isMoreOpen.value = value;
  };

  const isSwipeAware = ref<boolean>(false);
  const setSwipeAware = (state: boolean) => {
    isSwipeAware.value = state;
  };

  const isAddingEntry = ref<boolean>(false);
  const setAddingEntry = (state: boolean) => {
    isAddingEntry.value = state;
  };

  const isEditingEntry = ref<boolean>(false);
  const setEditingEntry = (state: boolean) => {
    isEditingEntry.value = state;
  };

  const isChatOpen = computed(() => route.query.chat === 'open');
  const isModalChatExperience = computed(() => isMobile.value === true && isChatOpen.value === true);

  // Event signals — increment to notify subscribers via watch()
  const refreshResultsSignal = ref(0);
  const refreshResults = () => {
    refreshResultsSignal.value++;
  };

  const gotoNewProposalSignal = ref(0);
  const gotoNewProposal = () => {
    gotoNewProposalSignal.value++;
  };

  const openResultListSignal = ref(0);
  const openResultList = () => {
    openResultListSignal.value++;
  };

  const addSpotSignal = ref(0);
  const addSpot = () => {
    addSpotSignal.value++;
  };

  const openAvailabilitiesSignal = ref(0);
  const openAvailabilities = () => {
    openAvailabilitiesSignal.value++;
  };

  const navigateToCitySignal = ref(0);
  const navigateToCity = () => {
    navigateToCitySignal.value++;
  };

  const loadCheckIn = (): ActiveCheckIn | null => {
    try {
      const raw = window.sessionStorage.getItem(StorageKeyType.ActiveCheckIn);
      return raw ? (JSON.parse(raw) as ActiveCheckIn) : null;
    } catch {
      return null;
    }
  };

  const resolveEndsAt = (checkIn: ActiveCheckIn): Date => {
    if (checkIn.endsAt) {
      return new Date(checkIn.endsAt);
    }

    return addHours(new Date(checkIn.checkedInAt), 2);
  };

  const withEndsAt = (checkIn: ActiveCheckIn): ActiveCheckIn => {
    if (checkIn.endsAt) {
      return checkIn;
    }

    return {
      ...checkIn,
      endsAt: addHours(new Date(checkIn.checkedInAt), 2).toISOString(),
    };
  };

  const activeCheckIn = ref<ActiveCheckIn | null>(loadCheckIn());

  const isCheckedIn = computed(() => {
    if (!activeCheckIn.value) return false;
    return isAfter(resolveEndsAt(activeCheckIn.value), new Date());
  });

  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  let expiryPoll: ReturnType<typeof setInterval> | null = null;

  const forgetCheckIn = () => {
    activeCheckIn.value = null;
    window.sessionStorage.removeItem(StorageKeyType.ActiveCheckIn);
  };

  const stopExpiryWatch = () => {
    if (expiryTimer) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    if (expiryPoll) {
      clearInterval(expiryPoll);
      expiryPoll = null;
    }
  };

  // A backgrounded tab suspends its timers, so expiry is re-evaluated against the clock
  // rather than trusted to fire on schedule.
  const expireIfElapsed = () => {
    if (!activeCheckIn.value) return;
    if (isAfter(resolveEndsAt(activeCheckIn.value), new Date())) return;
    stopExpiryWatch();
    forgetCheckIn();
  };

  const scheduleExpiry = (checkIn: ActiveCheckIn) => {
    stopExpiryWatch();
    const remaining = resolveEndsAt(checkIn).getTime() - Date.now();
    if (remaining <= 0) {
      forgetCheckIn();
      return;
    }
    expiryTimer = setTimeout(expireIfElapsed, remaining);
    expiryPoll = setInterval(expireIfElapsed, EXPIRY_POLL_MS);
  };

  const onWake = () => {
    if (document.visibilityState === 'visible') expireIfElapsed();
  };

  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', expireIfElapsed);
  }

  if (activeCheckIn.value) {
    scheduleExpiry(activeCheckIn.value);
  }

  const setActiveCheckIn = (checkIn: ActiveCheckIn) => {
    const next = withEndsAt(checkIn);
    activeCheckIn.value = next;
    window.sessionStorage.setItem(StorageKeyType.ActiveCheckIn, JSON.stringify(next));
    scheduleExpiry(next);
  };

  const clearActiveCheckIn = () => {
    stopExpiryWatch();
    forgetCheckIn();
  };

  /** Drops the pill once the server stops reporting the user as checked in at that spot. */
  const reconcileActiveCheckIn = (locationId: string, isCheckedInAtLocation: boolean) => {
    if (isCheckedInAtLocation) return;

    const current = activeCheckIn.value;
    if (current?.locationId !== locationId) return;

    // A refresh already in flight when the user checked in reports the state from before it.
    if (Date.now() - new Date(current.checkedInAt).getTime() < RECONCILE_GRACE_MS) return;

    clearActiveCheckIn();
  };

  return {
    navItems,
    setNavItems,
    getNavItems,
    getNavItemsBottom,
    getNavItemsMore,
    setMenuMessageCount,

    isCollapsed,
    isModalChatExperience,

    isLoading,
    setLoading,

    isNavVisible,
    setNavVisible,

    isMoreOpen,
    setMoreOpen,

    isAddingEntry,
    setAddingEntry,

    isEditingEntry,
    setEditingEntry,

    isSwipeAware,
    setSwipeAware,

    refreshResultsSignal,
    refreshResults,

    gotoNewProposalSignal,
    gotoNewProposal,

    openResultListSignal,
    openResultList,

    addSpotSignal,
    addSpot,

    openAvailabilitiesSignal,
    openAvailabilities,

    navigateToCitySignal,
    navigateToCity,

    activeCheckIn,
    isCheckedIn,
    setActiveCheckIn,
    clearActiveCheckIn,
    reconcileActiveCheckIn,
  };
});
